import {
  Component,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { TranslateService } from '@ngx-translate/core';
import {
  AppUser,
  AppUserAuth,
  AuthService,
  CookieService,
  EncryptedUser,
  LogService,
} from '@ubie/core';

const DEFAULT_LANGUAGE_ID = 1;
@Component({
  selector: 'shared-login-v2',
  templateUrl: './login-v2.component.html',
  styleUrls: ['./login-v2.component.scss'],
  standalone: false,
})

/**
 * Settings page: login form
 * --------
 * FLOW
 * --------
 * Enter username/pw to login
 * Emit event when register clicked
 * Emit event when forgot pw clicked
 */
export class LoginV2Component implements OnInit {
  private readonly securityService = inject(AuthService);
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly logService = inject(LogService);
  private readonly translateService = inject(TranslateService);

  private readonly cookieService = inject(CookieService);

  /////////////////////////////////////////////////////////
  //#region PROPERTIES
  /////////////////////////////////////////////////////////

  @Input() languageType = 'client';

  @Input() loginRedirectPath = '/tabs/dashboard';

  // Form formgroup
  form?: UntypedFormGroup;
  // Keep track of submit attempt
  formSubmitted = false;
  // Emit event when register button is clicked
  @Output() readonly registerClicked: EventEmitter<any> = new EventEmitter();
  // Emit event when forgot password button is clicked
  @Output() readonly forgotPasswordClicked: EventEmitter<any> =
    new EventEmitter();
  // Emit event when user logged in
  @Output() readonly userLoginSucceeded: EventEmitter<any> = new EventEmitter();
  // Emit event when user failed to log in
  @Output() readonly userLoginFailed: EventEmitter<any> = new EventEmitter();
  // Path to redirect to
  returnUrl: string | null = null;
  // Use showFullPage="True" to show the component page filling
  @Input() showFullPage?: boolean;
  // Use showRegister="True" to enable the register button on the form
  @Input() showRegister?: boolean;
  // Use showForgotPassword="True" to enable the forgot password button on the form
  @Input() showForgotPassword?: boolean;
  // grid sizes
  @Input() customGridSize: any;
  // Use this to set the logo on the login component
  _logo = 'https://imgplaceholder.com/300x160';
  @Input()
  set logo(data: string) {
    this._logo = data;
  }
  // User to work with when logging in
  user: AppUser = new AppUser();
  // Security object to work with once logged in
  securityObject: AppUserAuth | null = null;

  // default columns sizes
  sizeXSLeft = 2;
  sizeXSMid = 8;
  sizeXSRight = 2;

  sizeMDLeft = 4;
  sizeMDMid = 4;
  sizeMDRight = 4;

  @HostBinding('class.idb-theme')
  get isIdbTheme(): boolean {
    return this.languageType === 'admin';
  }

  //#endregion
  /////////////////////////////////////////////////////////

  constructor() {
    this.initForm();
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    if (!Capacitor.isNativePlatform()) {
      this.checkIfOldUiCookieIsPresent();
    }

    // fetch the return url from the route
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');

    // set custom grid sizes if available
    if (this.customGridSize && Object.keys(this.customGridSize).length > 0) {
      this.sizeXSLeft =
        this.customGridSize.sizeXSLeft > 0
          ? this.customGridSize.sizeXSLeft
          : this.sizeXSLeft;
      this.sizeXSMid =
        this.customGridSize.sizeXSMid > 0
          ? this.customGridSize.sizeXSMid
          : this.sizeXSMid;
      this.sizeXSRight =
        this.customGridSize.sizeXSRight > 0
          ? this.customGridSize.sizeXSRight
          : this.sizeXSRight;

      this.sizeMDLeft =
        this.customGridSize.sizeMDLeft > 0
          ? this.customGridSize.sizeMDLeft
          : this.sizeMDLeft;
      this.sizeMDMid =
        this.customGridSize.sizeMDMid > 0
          ? this.customGridSize.sizeMDMid
          : this.sizeMDMid;
      this.sizeMDRight =
        this.customGridSize.sizeMDRight > 0
          ? this.customGridSize.sizeMDRight
          : this.sizeMDRight;
    }
  }

  /**
   * This function checks if there is a cookie present from the old UI which
   * contains the current user. If the cookie is present, it will be parsed and
   * the user will be logged in using the security service.
   *
   * @private
   */
  private checkIfOldUiCookieIsPresent(): void {
    // check if old ui cookie is present
    const cookie = this.cookieService.getCookie('currentUser');
    if (cookie !== null) {
      try {
        //parsing the current user of the old ui
        const cookieDecoded = decodeURIComponent(cookie);
        const currentUser = JSON.parse(cookieDecoded);

        if (currentUser) {
          if (currentUser.EncryptedEmail && currentUser.EncryptedPassword) {
            const encryptedUser: EncryptedUser = {
              email: currentUser.EncryptedEmail,
              password: currentUser.EncryptedPassword,
            };
            this.cookieService.deleteCookieOnPath('currentUser', '/');

            this.securityService.loginEncrypted(encryptedUser).subscribe({
              next: (appUserAuth) => {
                this.userLoginSucceeded.emit();
                this.router.navigateByUrl(this.loginRedirectPath);
              },
              error: (error) => {
                this.logService.error(
                  'error getting current User of old ui',
                  error,
                );
                this.loginFailed();
              },
            });
          }
        }
      } catch (e) {
        this.logService.error('error getting current User of old ui', e);
      }
    } else {
      this.logService.info('old ui cookie is not present');
    }
  }

  /**
   * Init the form
   */
  private initForm(): void {
    // is only initiated once
    if (this.form === undefined) {
      this.form = this.formBuilder.group({
        username: [null, [Validators.required, Validators.email]],
        password: [null, Validators.required],
      });
    }
  }

  /**
   * Register button is clicked
   */
  onRegisterClicked(): void {
    this.registerClicked.emit();
  }

  /**
   * Forgot password button is clicked
   */
  onForgotPasswordClicked(): void {
    this.forgotPasswordClicked.emit();
  }

  pwdChange(event: any): void {
    this.form?.get('password')?.setValue(event.target.value);
  }

  loginFailed(): void {
    this.logService.info('received login error: ');
    // Emit user failed to log in event
    this.userLoginFailed.emit();
    // Reset security object
    this.securityObject = new AppUserAuth();
  }

  /**
   * Login button is clicked
   */
  onSubmit(): void {
    this.formSubmitted = true;
    // set form values
    this.form?.patchValue(
      {
        username: this.form.value['username'].trim(),
      },
      {
        emitEvent: false,
      },
    );

    if (this.form?.valid) {
      // Copy the form values over the object values
      const obj: AppUser = Object.assign({}, this.user, this.form.value);

      obj.password = obj.password.trim();

      this.securityService.loginMqtt(obj).subscribe({
        next: (data) => {
          this.logService.info(
            'received login mqtt data: ' + JSON.stringify(data),
          );
          this.securityObject = data;
          const languageId = this.securityObject.language?.id;
          this.translateService.use(
            `${this.languageType}_${languageId ?? DEFAULT_LANGUAGE_ID}`,
          );
          // Emit user logged in event
          this.userLoginSucceeded.emit();

          if (this.returnUrl) {
            // Navigate to the returnurl
            this.router.navigateByUrl(this.returnUrl, { replaceUrl: true });
          } else {
            // Navigate to the default path
            this.router.navigateByUrl(this.loginRedirectPath);
          }
        },
        error: (error) => {
          this.loginFailed();
        },
      });
    }
  }
}
